/**
 * V2 Step 5 — Branding (final input step).
 * Design from v2-screens-3.jsx:
 *  - Live public-page preview at top
 *  - Title field (required)
 *  - Logo upload card (dashed when empty, filled when uploaded)
 *  - Emoji picker (optional override — initials are the default)
 *  - Bio textarea (optional, 160 char)
 */
import * as React from 'react';
import { useRef } from 'react';
import { V2C, fontBody } from '../shared/tokens';
import { V2Input, V2Textarea, V2Btn, V2Label } from '../shared/primitives';
import V2Step from '../shared/V2Step';
import V2PublicPreview from '../shared/V2PublicPreview';
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

interface Props {
  formData: BusinessSignupData;
  update: (patch: Partial<BusinessSignupData>) => void;
  onBack?: () => void;
  onNext: () => void;
  onLaunch: () => Promise<void>;
  isSaving: boolean;
  stepIdx: number;
  totalSteps: number;
  animDir: 'forward' | 'back';
  validationError: string;
}

const LOGO_EMOJIS = ['💇', '✂️', '💅', '🌿', '🏋', '🧭', '📚', '🫧', '🎨', '🍃'];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export default function V2BrandingStep({ formData, update, onBack, onNext, onLaunch, isSaving, stepIdx, totalSteps, animDir, validationError }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasLogo = !!formData.logoPreview;
  const hasEmoji = !!formData.logoEmoji;

  function handleLogoSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      alert('Logo must be under 2MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => {
      update({ logoFile: file, logoPreview: ev.target?.result as string, logoEmoji: '' });
    };
    reader.readAsDataURL(file);
  }

  function removeLogo() {
    update({ logoFile: null, logoPreview: '', logoEmoji: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function clearEmoji() {
    update({ logoEmoji: '' });
  }

  return (
    <V2Step
      stepIdx={stepIdx}
      totalSteps={totalSteps}
      title="Make it yours"
      subtitle="A short title goes on your booking page. Bio and logo are optional."
      onBack={onBack}
      animDir={animDir}
    >
      {/* Live preview */}
      <V2PublicPreview formData={formData} />

      {/* Title — required */}
      <V2Input
        label="Title or profession"
        value={formData.title}
        onChange={v => update({ title: v })}
        placeholder="Master barber · 8 years · Brooklyn"
        error={validationError && !formData.title?.trim() ? 'Add a title so clients know what you do.' : null}
        hint={formData.title?.trim() ? undefined : 'Keep it short and human — clients see this.'}
        autoFocus
        onboardingField="title"
      />

      {/* Logo */}
      <V2Label optional>Logo</V2Label>
      <input
        ref={fileInputRef}
        type="file"
        data-onboarding-field="logo_upload"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        onChange={handleLogoSelect}
        style={{ display: 'none' }}
      />

      {hasLogo ? (
        <div style={{
          background: '#fff', borderRadius: 14, border: `1px solid ${V2C.line}`,
          padding: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 12, flexShrink: 0,
            backgroundImage: `url(${formData.logoPreview})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            border: `1px solid ${V2C.line}`,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: V2C.ink, fontFamily: fontBody }}>Logo uploaded</div>
            <div style={{ fontSize: 12, color: V2C.mute, fontFamily: fontBody }}>
              {formData.logoFile ? 'Looking sharp.' : 'Re-upload before launching to save the latest version.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button onClick={() => fileInputRef.current?.click()} style={smallBtnStyle}>
              Replace
            </button>
            <button onClick={removeLogo} style={{ ...smallBtnStyle, color: V2C.err, borderColor: V2C.errSoft }}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{
            background: '#fff', borderRadius: 14,
            border: `1.5px dashed ${V2C.brand}66`,
            padding: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12,
            cursor: 'pointer', transition: 'all 0.2s',
          }}
        >
          <div style={{
            width: 52, height: 52, borderRadius: 12, flexShrink: 0,
            background: V2C.brandSoft,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
            color: V2C.brand, fontWeight: 700,
          }}>
            {hasEmoji ? formData.logoEmoji : <InitialsPlaceholder name={formData.fullName || formData.businessName || ''} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: V2C.ink, marginBottom: 2, fontFamily: fontBody }}>
              Upload a logo
            </div>
            <div style={{ fontSize: 12, color: V2C.mute, fontFamily: fontBody }}>
              PNG · JPG · SVG · max 2MB
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
            style={{
              padding: '8px 14px', borderRadius: 10,
              border: `1px solid ${V2C.lineHard}`, background: '#fff',
              color: V2C.ink, fontFamily: fontBody, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            Choose file
          </button>
        </div>
      )}

      {/* Emoji picker — only show when no logo */}
      {!hasLogo && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: V2C.mute, letterSpacing: 0.4, fontWeight: 600, fontFamily: fontBody }}>
              Or pick an emoji — initials show by default
            </div>
            {hasEmoji && (
              <button
                onClick={clearEmoji}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 11, color: V2C.brand, fontWeight: 600, padding: '2px 0',
                  fontFamily: fontBody, display: 'flex', alignItems: 'center', gap: 3,
                }}
              >
                ✕ Use my initials
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            {LOGO_EMOJIS.map(e => (
              <button
                key={e}
                onClick={() => update({ logoEmoji: formData.logoEmoji === e ? '' : e })}
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  border: formData.logoEmoji === e ? `1.5px solid ${V2C.brand}` : `1px solid ${V2C.line}`,
                  background: formData.logoEmoji === e ? V2C.brandSoft : '#fff',
                  cursor: 'pointer', fontSize: 16, padding: 0,
                }}
              >
                {e}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Bio */}
      <V2Textarea
        label="Short bio"
        optional
        value={formData.bio}
        onChange={v => update({ bio: v })}
        placeholder="Two sentences. What you do, who it's for."
        maxLength={160}
        rows={3}
        onboardingField="bio"
      />

      {/* Launch readiness checklist */}
      <LaunchChecklist formData={formData} />

      {validationError && (
        <div style={{ color: V2C.err, fontSize: 13, fontWeight: 600, marginBottom: 8, fontFamily: fontBody }}>
          {validationError}
        </div>
      )}

      <div style={{ position: 'sticky', bottom: 0, paddingBottom: 24, paddingTop: 8, background: 'linear-gradient(to bottom, transparent, #F3F3FF 60%)' }}>
        <V2Btn onClick={onLaunch} loading={isSaving} style={{ height: 60, fontSize: 17 }}>
          {isSaving ? 'Launching…' : 'Launch my booking page'}
        </V2Btn>
      </div>
    </V2Step>
  );
}

// ── Launch readiness checklist ─────────────────────────────────────────────────
function LaunchChecklist({ formData }: { formData: BusinessSignupData }) {
  const hasBookingUrl = !!formData.bookingUrl?.trim();
  const hasServices   = (formData.services?.length ?? 0) > 0;
  const hasHours      = formData.workingHours
    ? Object.values(formData.workingHours).some(d => d?.enabled)
    : false;
  const hasTitle      = !!formData.title?.trim();

  const checks: { label: string; done: boolean }[] = [
    { label: `Booking link: getfiggy.link/${formData.bookingUrl || '…'}`, done: hasBookingUrl },
    { label: `${formData.services?.length ?? 0} service${(formData.services?.length ?? 0) !== 1 ? 's' : ''} added`,       done: hasServices },
    { label: 'Working hours set',          done: hasHours },
    { label: 'Public page customized',     done: hasTitle },
  ];

  const allDone = checks.every(c => c.done);

  return (
    <div style={{
      background: allDone ? V2C.okBg : V2C.bgAlt,
      border: `1px solid ${allDone ? V2C.ok + '44' : V2C.line}`,
      borderRadius: 14, padding: '14px 16px', marginBottom: 16,
      transition: 'all 0.3s ease',
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: allDone ? V2C.ok : V2C.mute, letterSpacing: 0.4, marginBottom: 8, fontFamily: fontBody }}>
        {allDone ? 'READY TO LAUNCH' : 'ALMOST THERE'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {checks.map(({ label, done }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 16, height: 16, borderRadius: 99, flexShrink: 0,
              background: done ? V2C.ok : V2C.line,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {done && (
                <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                  <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: 13, color: done ? V2C.ink : V2C.mute, fontFamily: fontBody }}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InitialsPlaceholder({ name }: { name: string }) {
  const initials = (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
  return <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.5 }}>{initials || '?'}</span>;
}

const smallBtnStyle: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 8,
  border: `1px solid ${V2C.line}`, background: '#fff',
  color: V2C.ink, fontFamily: fontBody, fontSize: 12, fontWeight: 600,
  cursor: 'pointer',
};
